import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Phone, Mail, Instagram, Facebook } from 'lucide-react';

export const Layout = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    {
      name: 'Company',
      submenu: [
        { name: 'About', href: '/about' },
        { name: 'Careers', href: '/careers' },
      ],
    },
    {
      name: 'Services',
      submenu: [
        { name: 'Shower Glass', href: '/shower-glass' },
        { name: 'Mirrors', href: '/mirrors' },
        { name: 'Wine Enclosures', href: '/wine-enclosures' },
        { name: 'Office Partitions', href: '/office-partitions' },
        { name: 'Glass Railings', href: '/glass-railings' },
        { name: 'Storefront & Curtain Walls', href: '/storefront-curtain-walls' },
      ],
    },
    { name: 'Portfolio', href: '/portfolio' },
    {
      name: 'Resources',
      submenu: [
        { name: 'The Glass Spec', href: '/blog', description: 'Technical blog' },
        { name: 'Downloads', href: '/resources', description: 'PDFs & catalogs' },
        { name: 'FAQ', href: '/faq', description: 'Common questions' },
      ],
    },
    { name: 'Contact', href: '/contact' },
  ];

  // Generic dropdown component for any submenu
  const NavDropdown = ({ item, mobile = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef(null);

    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setIsOpen(true);
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 300);
    };

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    if (mobile) {
      return (
        <div className="space-y-2">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-base font-medium text-foreground hover:text-primary transition-colors w-full text-left flex items-center justify-between"
          >
            {item.name}
            <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isOpen && (
            <div className="pl-4 space-y-2">
              {item.submenu.map((subItem) => (
                <Link
                  key={subItem.name}
                  to={subItem.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {subItem.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        className="relative group"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button className="text-sm font-medium text-foreground hover:text-primary transition-colors flex items-center gap-1">
          {item.name}
          <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 glass-strong rounded-lg shadow-lg py-2 z-50">
            {item.submenu.map((subItem) => (
              <Link
                key={subItem.name}
                to={subItem.href}
                className="block px-4 py-2 text-sm text-foreground hover:text-primary hover:bg-muted/50 transition-colors"
              >
                <span>{subItem.name}</span>
                {subItem.description && (
                  <span className="block text-xs text-muted-foreground">{subItem.description}</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-strong shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/images/logos/GLASS.png" 
                alt="Comal Glass Company" 
                className="h-[4.75rem] w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) =>
                item.submenu ? (
                  <NavDropdown key={item.name} item={item} />
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-sm font-medium transition-colors ${
                      location.pathname === item.href
                        ? 'text-primary'
                        : 'text-foreground hover:text-primary'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              )}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <Button variant="ghost" size="sm" asChild>
                <a href="tel:8303599925" className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>(830) 359-9925</span>
                </a>
              </Button>
              <Button size="sm" asChild className="bg-primary hover:bg-primary/90 glow-hover">
                <Link to="/contact">Get Quote</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <nav className="flex flex-col space-y-6 mt-8">
                  {navigation.map((item) =>
                    item.submenu ? (
                      <NavDropdown key={item.name} item={item} mobile />
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`text-base font-medium transition-colors ${
                          location.pathname === item.href
                            ? 'text-primary'
                            : 'text-foreground hover:text-primary'
                        }`}
                      >
                        {item.name}
                      </Link>
                    )
                  )}
                  <div className="pt-6 border-t space-y-4">
                    <Button variant="outline" className="w-full" asChild>
                      <a href="tel:8303599925" className="flex items-center justify-center space-x-2">
                        <Phone className="h-4 w-4" />
                        <span>(830) 359-9925</span>
                      </a>
                    </Button>
                    <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                      <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                        Get Quote
                      </Link>
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">{children}</main>

      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground mt-20">
        <div className="container-custom py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <Link to="/" className="inline-block mb-4">
                <img 
                  src="/images/logos/Comal_Glass_Logo_White.png" 
                  alt="Comal Glass Company" 
                  className="h-12 w-auto"
                />
              </Link>
              <p className="text-sm text-muted-foreground mb-4">
                Leading provider of high-quality frameless shower glass, mirrors and architectural glass solutions.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/comalglass/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Follow us on Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href="https://www.facebook.com/comalglass/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-primary transition-colors"
                  aria-label="Follow us on Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
              <div className="flex flex-col space-y-2 text-sm mt-4">
                <a href="tel:8303599925" className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors">
                  <Phone className="h-4 w-4" />
                  <span>(830) 359-9925</span>
                </a>
                <a href="mailto:orders@comalglass.com" className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors">
                  <Mail className="h-4 w-4" />
                  <span>orders@comalglass.com</span>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-secondary-foreground">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/portfolio" className="text-muted-foreground hover:text-primary transition-colors">
                    Portfolio
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-secondary-foreground">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/shower-glass" className="text-muted-foreground hover:text-primary transition-colors">
                    Shower Glass
                  </Link>
                </li>
                <li>
                  <Link to="/wine-enclosures" className="text-muted-foreground hover:text-primary transition-colors">
                    Wine Enclosures
                  </Link>
                </li>
                <li>
                  <Link to="/office-partitions" className="text-muted-foreground hover:text-primary transition-colors">
                    Office Partitions
                  </Link>
                </li>
                <li>
                  <Link to="/mirrors" className="text-muted-foreground hover:text-primary transition-colors">
                    Mirrors
                  </Link>
                </li>
                <li>
                  <Link to="/storefront-curtain-walls" className="text-muted-foreground hover:text-primary transition-colors">
                    Storefront & Curtain Walls
                  </Link>
                </li>
              </ul>
            </div>

            {/* Business Hours */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-secondary-foreground">Business Hours</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Monday - Friday: 8:00 AM - 5:00 PM</li>
                <li>Saturday: By Appointment</li>
                <li>Sunday: Closed</li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-border/20 mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Comal Glass Company. All rights reserved.</p>
            <p className="mt-2">
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
